import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import { OrganicChartTooltip } from "src/dashboards/paidMedia/components/metaAds/metaChartConfig";
import {
  CAMPAIGN_PAGE_SIZE,
  CAMPAIGN_PERFORMANCE_METRICS,
  type CampaignPerformanceDatum,
  type CampaignPerformanceMetricKey,
} from "../../data/engagementOverviewTypes";
import {
  formatAxisPercent,
  formatTooltipCompact,
  formatTooltipPercent,
  truncateLabel,
} from "../../utils/engagementOverviewFormat";
import ChartLegendChips from "./ChartLegendChips";
import EngagementEmptyState from "./EngagementEmptyState";
import EngagementSectionPagination from "./EngagementSectionPagination";
import { getEngagementTooltipInfo } from "./getEngagementTooltipInfo";

export interface CampaignPerformanceOverviewProps {
  data: CampaignPerformanceDatum[];
  sectionTitle?: string;
  tooltipKey?: string;
  legendAriaLabel?: string;
  paginationAriaLabel?: string;
}

const VALUE_KEY: Record<CampaignPerformanceMetricKey, keyof CampaignPerformanceDatum> = {
  openRate: "openRateValue",
  clickRate: "clickRateValue",
  totalSent: "totalSentValue",
  clickThroughRate: "clickThroughRateValue",
};

const CampaignPerformanceOverview: FC<CampaignPerformanceOverviewProps> = ({
  data,
  sectionTitle,
  tooltipKey = "campaignPerformance",
  legendAriaLabel,
  paginationAriaLabel,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedSectionTitle =
    sectionTitle ?? t("engagementOverview.shared.campaignPerformanceSectionTitle");
  const resolvedLegendAriaLabel =
    legendAriaLabel ?? t("engagementOverview.shared.campaignPerformanceLegendAria");
  const resolvedPaginationAriaLabel =
    paginationAriaLabel ?? t("engagementOverview.shared.campaignPerformancePaginationAria");
  const [page, setPage] = useState(1);
  const [activeMetrics, setActiveMetrics] = useState<CampaignPerformanceMetricKey[]>(
    CAMPAIGN_PERFORMANCE_METRICS.map(metric => metric.key),
  );

  const legendItems = useMemo(
    () =>
      CAMPAIGN_PERFORMANCE_METRICS.map(metric => ({
        key: metric.key,
        label: t(metric.labelKey),
        color: metric.color,
      })),
    [t],
  );

  const safeData = useMemo(() => data ?? [], [data]);
  const totalPages = Math.max(1, Math.ceil(safeData.length / CAMPAIGN_PAGE_SIZE));

  const pageData = useMemo(() => {
    const start = (page - 1) * CAMPAIGN_PAGE_SIZE;
    return safeData.slice(start, start + CAMPAIGN_PAGE_SIZE);
  }, [safeData, page]);

  const toggleMetric = useCallback((key: string) => {
    setActiveMetrics(current => {
      const typedKey = key as CampaignPerformanceMetricKey;
      if (current.includes(typedKey)) {
        if (current.length === 1) {
          return current;
        }
        return current.filter(item => item !== typedKey);
      }
      return [...current, typedKey];
    });
  }, []);

  return (
    <article className="engagement-overview-panel engagement-overview-panel--chart">
      <div className="engagement-overview-panel__header engagement-overview-panel__header--with-legend">
        <div className="engagement-overview-panel__heading">
          <h3 className="engagement-overview-panel__heading-title">{resolvedSectionTitle}</h3>
          <DefinitionTooltip
            label={resolvedSectionTitle}
            info={getEngagementTooltipInfo(t, tooltipKey)}
          />
        </div>
        <ChartLegendChips
          items={legendItems}
          activeKeys={activeMetrics}
          onToggle={toggleMetric}
          ariaLabel={resolvedLegendAriaLabel}
        />
      </div>

      {!safeData.length ? (
        <EngagementEmptyState
          message={t("engagementOverview.shared.campaignPerformanceEmptyMessage")}
        />
      ) : (
        <>
          <div className="engagement-overview-chart engagement-overview-chart--grouped">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={pageData}
                barGap={2}
                barCategoryGap="2%"
                margin={{ top: 6, right: 4, bottom: 4, left: 0 }}
                maxBarSize={28}
              >
                <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tickFormatter={value => truncateLabel(value, 14)}
                  tick={{ fontSize: 11, fill: "#60718b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  width={40}
                  tick={{ fontSize: 11, fill: "#60718b" }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                  tickFormatter={formatAxisPercent}
                />
                <Tooltip
                  content={({ active, label, payload }) => (
                    <OrganicChartTooltip
                      active={active}
                      label={label}
                      labelTitle={t("engagementOverview.shared.campaignTooltipLabel")}
                      payload={payload}
                      seriesLabelForDataKey={key => {
                        const metric = CAMPAIGN_PERFORMANCE_METRICS.find(
                          item => item.key === key,
                        );
                        return metric ? t(metric.labelKey) : key;
                      }}
                      valueFormatter={(_value, name) => {
                        const row = pageData.find(item => item.label === label);
                        if (!row) {
                          return "—";
                        }
                        if (name === "totalSent") {
                          return formatTooltipCompact(row.totalSent, i18n.language);
                        }
                        if (name === "openRate") {
                          return formatTooltipPercent(row.openRate, i18n.language);
                        }
                        if (name === "clickRate") {
                          return formatTooltipPercent(row.clickRate, i18n.language);
                        }
                        if (name === "clickThroughRate") {
                          return formatTooltipPercent(row.clickThroughRate, i18n.language);
                        }
                        return String(_value ?? "");
                      }}
                    />
                  )}
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                {CAMPAIGN_PERFORMANCE_METRICS.filter(metric =>
                  activeMetrics.includes(metric.key),
                ).map(metric => (
                  <Bar
                    key={metric.key}
                    dataKey={VALUE_KEY[metric.key] as string}
                    fill={metric.color}
                    barSize={28}
                    radius={[5, 5, 0, 0]}
                    name={metric.key}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="engagement-overview-panel__pagination-right">
            <EngagementSectionPagination
              currentPage={page}
              totalPages={totalPages}
              onPrev={() => setPage(current => Math.max(1, current - 1))}
              onNext={() => setPage(current => Math.min(totalPages, current + 1))}
              ariaLabel={resolvedPaginationAriaLabel}
            />
          </div>
        </>
      )}
    </article>
  );
};

export default CampaignPerformanceOverview;
